import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DOCTYPE } from "../../_constants/docTypeConstants";
import { getPermissions } from "../../_helpers/authorize";
import { docPermConstants } from "../../_constants/permissionConstants";
import "./styles/nav.css";
import { menuTypes } from "../../_constants/menuTypes";
import { actionScripts } from "../../_helpers/actionScripts";

class MainMenu extends PureComponent {
  state = {
    selectedMenuIds: [],
    expandedParentIds: [],
    postIconSelected: null,
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClick_outsideMenu);
    document.addEventListener("touchstart", this.handleClick_outsideMenu);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClick_outsideMenu);
    document.removeEventListener("touchstart", this.handleClick_outsideMenu);
  }

  setWrapperRef = (node) => {
    this.wrapperRef = node;
  };

  /**
   * @method handleClick_outsideMenu
   * @summary
   * Hides the menu when user clicks/taps outside of the menu
   * (Invokes onClose() provided in this.props.)
   * @param {Object} e
   */
  handleClick_outsideMenu = (e) => {
    if (
      this.wrapperRef &&
      !this.wrapperRef.contains(e.target) &&
      e.target.nodeName !== "path" &&
      e.target.nodeName !== "svg"
    )
      this.props.onClose();
  };

  /**
   * @method handleSelectedMenuId
   * @summary Update the selectedMenuIds array
   * @param {number} selectedMenuId
   * @param {number} depthLevel
   * @returns {function} Callback
   */
  handleSelectedMenuId = (selectedMenuId, depthLevel) => () => {
    const updatedArray = this.state.selectedMenuIds.slice(0);
    updatedArray[depthLevel] = selectedMenuId;
    this.setState({
      selectedMenuIds: updatedArray,
    });
  };

  /**
   * @method handleClick_postIcon
   * @summary Trigger action corresponding to the clicked postIcon
   * @param {string} postIconAction
   * @param {MenuOption} option
   * @returns {function(React.SyntheticEvent)} Callback
   */
  handleClick_postIcon = (postIconAction, option) => (e) => {
    const { user } = this.props;
    const { projectId } = option; // Note: for postIcons, do not inherit option's docType.

    //       Create a new docType for each postIcon's action.
    switch (postIconAction) {
      case "CREATE_ADR": {
        actionScripts.CREATE_ADR_V2.run(user.userId, projectId);
        break;
      }
      case "FETCH_ADR":
        break;
      case "COPY_ADR": {
        break;
      }
      default:
        break;
    }
  };

  /**
   * @method handleClick_menuItem
   * @summary Handler for non-postIcon menu selection.
   * Uses e.target.nodeName to distinguish whether selected option is
   * postIcon or not. Must check if the selected option is parent,
   * because selection of children bubbles up to the parent and
   * triggers the parent's click action.
   * @param {boolean} hasChildren
   * @param {MenuOption} option
   * @returns {function(Object)} Callback
   */
  handleClick_menuItem = (hasChildren, option) => (e) => {
    // Prevent propagation of selection from children
    e.preventDefault();
    const { user, onClose } = this.props;
    if (!option.isDisabled) {
      if (!hasChildren && option.type !== "MENU_SEPARATOR") {
        const targetEl = e.target.nodeName.toLowerCase();

        // If the selected option is not a postIcon...
        if (targetEl === "div" || targetEl === "li") {
          // Close menu
          onClose();
          const { projectId, docType, label } = option;

          // Switch docType from selected option.
          switch (docType) {
            case DOCTYPE.LINK: {
              const authRequest = { user, docType, projectId: 0, creatorId: 0 };
              if (getPermissions(authRequest).includes(docPermConstants.VIEW))
                actionScripts.OPEN_LINK.run(label);
              else alert("Unauthorized!");
              break;
            }
            case DOCTYPE.REPORT_SELECTION: {
              const authRequest = { user, docType, projectId: 0, creatorId: 0 };
              if (getPermissions(authRequest).includes(docPermConstants.VIEW))
                actionScripts.OPEN_REPORT_SELECTION.run();
              else alert("Unauthorized!");
              break;
            }
            case DOCTYPE.ADMIN_TASKS: {
              const authRequest = { user, docType, projectId: 0, creatorId: 0 };
              if (getPermissions(authRequest).includes(docPermConstants.VIEW))
                actionScripts.OPEN_ADMIN_TASKS.run();
              else alert("Unauthorized!");
              break;
            }
            case DOCTYPE.ADR_LIST:
              actionScripts.OPEN_ADR_LIST.run(projectId);
              break;
            case DOCTYPE.PROJECT_SETUP:
              actionScripts.OPEN_PROJ_ADMIN.run(projectId);
              break;
            case DOCTYPE.PROJECT_DASH:
              actionScripts.OPEN_PROJ_DASH.run(projectId);
              break;
            case DOCTYPE.PROJECT_LIST:
              actionScripts.OPEN_PROJ_LIST.run(projectId);
              break;
            // case DOCTYPE.OVERTIME_LIST:
            //   actionScripts.OPEN_OVERTIME.run();
            //   break;
            case DOCTYPE.PROJECT_ASSIGNMENT:
              actionScripts.OPEN_PROJ_ASSIGNMENT.run();
              break;
            case DOCTYPE.QMS_DASHBOARD:
              actionScripts.OPEN_QMS_DASH.run();
              break;
            default:
              break;
          }
        } else if (targetEl === "svg" || targetEl === "path") {
          // Close menu
          onClose();
          // Action from postIcon click is handled by handleClick_postIconSelection()
        }
      } else {
        // If parent, check if its submenu is expanded
        if (this.state.selectedMenuIds[0] === option.menuId) {
          const expandedIndex = this.state.expandedParentIds.indexOf(option.menuId);
          // If false, add parent's menuId to expandedParentId[]
          if (expandedIndex === -1)
            this.setState({
              expandedParentIds: [...this.state.expandedParentIds, option.menuId],
            });
          // If true, remove menuId from expandedParentId[]
          else {
            let expandedParentIds = this.state.expandedParentIds.slice(0);
            expandedParentIds.splice(expandedIndex, 1);
            this.setState({ expandedParentIds });
          }
        }
      }
    }
  };

  /**
   * @method parentIsExpanded
   * @summary Determines whether any parent menu options are expanded.
   * (Multiple parents could be open simultaneously)
   * @param {string} menuId
   * @returns {boolean}
   */
  parentIsExpanded = (menuId) => this.state.expandedParentIds.includes(menuId);

  /**
   * @method isChild
   * @summary Determines whether the menu option is a child
   * @param {MenuOption} option
   * @returns {boolean}
   */
  isChild = (option) => option.type === "MENU_OPTION_CHILD";

  /**
   * @property getOptionClass
   * @summary Returns CSS class name for the provided option
   * @param {MenuOption} option
   * @returns {string}
   */
  getOptionClass = (option) => {
    switch (option.type) {
      case menuTypes.MENU_OPTION_ROOT:
      case menuTypes.MENU_OPTION_CHILD:
        if (option.isDisabled) return "dms-main-menu-option-disabled";
        else return "dms-main-menu-option";
      case "MENU_PARENT":
        return "dms-main-menu-parent";
      case "MENU_SEPARATOR":
        return "dms-main-menu-separator my-1";
      default:
        return "dms-main-menu-option-not-found";
    }
  };

  // All preIcon's icon imports must be registered in mainMenu.jsx.
  renderPreIcon = (option) => {
    if (option.preIcon)
      return <FontAwesomeIcon className="fa-fw mr-2" icon={option.preIcon} />;
  };

  /*
   * renderPostIcon: returns a sub-span element containing a postIcon.
   *
   * postIcons are not predefined - rather, this function accepts action as param
   * and selects postIcons based on the type of action selected.
   * Reason is that only a limited range of main menu-level DMS actions are anticipated.
   * Only 2 postIcons are used as of 11/2018 - "folder-open" (for open) and "file" (for new).
   */
  renderPostIcon = (action, option) => {
    const iconClass = "fa-fw float-right mr-2 mt-1";
    const actionVerb = action.split("_")[0];
    // Only 2 postIcons are allowed. Verbs may vary,
    // but as of 10/30/18 only "CREATE" and "FETCH" are in use.
    switch (actionVerb) {
      case "CREATE":
        return (
          <span key={action} className="dms-main-menu-post-icons float-right">
            <FontAwesomeIcon
              id="postIcon"
              key={action}
              className={iconClass}
              icon="file"
              onClick={this.handleClick_postIcon(action, option)}
            />
          </span>
        );
      case "FETCH":
        return (
          <span key={action} className="dms-main-menu-post-icons float-right">
            <FontAwesomeIcon
              id="postIcon"
              key={action}
              className={iconClass}
              icon="folder-open"
              onClick={this.handleClick_postIcon(action, option)}
            />
          </span>
        );
      case "COPY":
        return (
          <span key={action} className="dms-main-menu-post-icons float-right">
            <FontAwesomeIcon
              id="postIcon"
              key={action}
              className={iconClass}
              icon="clone"
              onClick={this.handleClick_postIcon(action, option)}
            />
          </span>
        );
      default:
        break;
    }
  };

  displayCaret = (option) => {
    if (option.type === "MENU_PARENT")
      if (this.parentIsExpanded(option.menuId))
        return <FontAwesomeIcon className="float-right mr-3 mt-2" icon={"angle-up"} />;
      else
        return <FontAwesomeIcon className="float-right mr-3 mt-2" icon={"angle-down"} />;
  };

  /**
   * Renders the main menu recursively based on the option tree
   *
   * @param {import("../../_mainMenu/mainMenuTypeDefs").MenuOption[]} options
   * @param {number} depthLevel - 0 for top-level option
   */
  renderMenu = (options, depthLevel = 0) => {
    const menuOptions = options.map((option) => {
      const display = option.label;
      const hasChildren = option.children && option.children.length > 0;

      // Recursive submenu rendering
      let subMenu;
      if (this.parentIsExpanded(option.menuId) && hasChildren) {
        const newDepthLevel = depthLevel + 1;
        subMenu = this.renderMenu(option.children, newDepthLevel);
      }

      /*
       * MainMenu structure:
       *   ul>li*option.length>div>span
       *
       *   Renders menu tree within div.dms-main-menu-body.
       *   Span element contains sub-spans with postIcons.
       */
      return (
        <li
          key={option.menuId}
          className={this.getOptionClass(option)}
          to={option.link}
          onMouseEnter={this.handleSelectedMenuId(option.menuId, depthLevel)}
          onClick={this.handleClick_menuItem(hasChildren, option)}
        >
          <div>
            {this.renderPreIcon(option)}
            {display}
            {option.isNew && <span className="badge badge-primary ml-2">New</span>}
            <span className="dms-main-menu-post-icons float-right" id="postIcons">
              {option.postIconActions && option.postIconActions.length > 0
                ? option.postIconActions.map((action) =>
                  this.renderPostIcon(action, option)
                )
                : ""}
            </span>
            {this.displayCaret(option)}

            {subMenu}
          </div>
        </li>
      );
    });
    return <ul className="dms-main-menu">{menuOptions}</ul>;
  };

  render() {
    return (
      <nav className="dms-main-menu-body" ref={this.setWrapperRef}>
        <div className="row">
          <div className="dms-main-menu-topper-left" />
          <div className="dms-main-menu-topper-right" />
        </div>
        {this.renderMenu(this.props.options)}
      </nav>
    );
  }
}

const mapStateToProps = (state) => ({
  options: state.mainMenu.options,
  user: state.authentication.user,
});

export default connect(mapStateToProps)(MainMenu);